import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { isNative } from '../services/platformService'

/**
 * Single house-style page entrance: 160ms opacity-only fade-in on mount.
 * No translate, no per-section stagger - the full page appears together so
 * navigation between bottom-nav tabs feels snappy rather than choreographed.
 *
 * Honours `prefers-reduced-motion` (snap, no animation) and on iOS Capacitor
 * native we also snap, since the native push transition already provides
 * a sense of arrival; an in-page fade competes with it.
 *
 * Usage:
 *   <PageTransition>
 *     ...page contents...
 *   </PageTransition>
 *
 * Replaces the previous `<motion.div variants={containerVariants} initial="hidden"
 * animate="visible">` boilerplate that was repeated across every top-level
 * page along with per-section `motion.div variants={itemVariants}`.
 */
function PageTransition({ children, className = '' }) {
  const prefersReducedMotion = useReducedMotion()
  const snap = prefersReducedMotion || isNative()

  if (snap) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export default PageTransition
